import { Injectable } from '@angular/core';
import {
    Activity,
    ActivityEmail,
    ActivityEngagement,
    ActivityStatusType,
    ActivityType,
    CommunicationDirection,
    ConversationStatus,
} from '@wdx/clmi/clmi-swagger-gen';
import { WdxDateFormat, WdxDateTimeService } from '@wdx/shared/utils';
import { take } from 'rxjs/operators';
import { ReactiveFormDynamicDataService } from '../../../../../shared/services';

@Injectable({
    providedIn: 'root',
})
export class ToDoService {
    locale: string;

    constructor(
        private dateTimeService: WdxDateTimeService,
        private dynamicDataService: ReactiveFormDynamicDataService,
    ) {
        this.dynamicDataService
            .getMeLocale()
            .pipe(take(1))
            .subscribe((meLocale) => (this.locale = meLocale.code));
    }

    isCompleteActivity(activity: Activity): boolean {
        return activity?.status === ActivityStatusType.Complete;
    }

    isCancelledActivity(activity: Activity): boolean {
        return activity?.status === ActivityStatusType.Cancelled;
    }

    isDraft(activity: Activity): boolean {
        return activity.status === ActivityStatusType.Draft;
    }

    isAllDay(activity: Activity): boolean {
        return activity.isAllDay;
    }

    isEndedActivity(activity: Activity): boolean {
        return (
            (activity as ActivityEngagement)?.conversationStatus ===
            ConversationStatus.Ended
        );
    }

    isOverdueActivity(activity: Activity): boolean {
        const overdueDate = this.getActivityStatusDate(activity);
        if (!overdueDate || activity.activityType === ActivityType.Unblu) {
            return false;
        }
        return new Date().getTime() > new Date(overdueDate).getTime();
    }

    isEmail(activity: Activity): boolean {
        return activity.activityType === ActivityType.Email;
    }

    isSentEmailSmsOrLetter(activity: Activity & ActivityEmail): boolean {
        return (
            !this.isDraft(activity) &&
            this.isCompleteActivity(activity) &&
            activity.direction === CommunicationDirection.Outgoing &&
            [ActivityType.Email, ActivityType.Sms, ActivityType.Letter].includes(
                activity.activityType,
            )
        );
    }

    isReceivedEmail(activity: Activity & ActivityEmail): boolean {
        return (
            this.isEmail(activity) &&
            activity.direction === CommunicationDirection.Incoming
        );
    }

    isMessage(activity: Activity): boolean {
        return [
            ActivityType.Email,
            ActivityType.Glia,
            ActivityType.Qwil,
            ActivityType.Sms,
            ActivityType.SecureMessage,
            ActivityType.Unblu,
        ].includes(activity.activityType);
    }

    isTaskOrWorkItem(activity: Activity): boolean {
        return [ActivityType.WorkItem, ActivityType.Task].includes(
            activity.activityType,
        );
    }

    getFormattedActivityStatusDate(activity: Activity): string {
        if (this.isTaskOrWorkItem(activity)) {
            return this.dateTimeService.convertDateToViewFriendlyFormat(
                activity.due as unknown as string,
                {
                    format: WdxDateFormat.RelativeDate,
                    locale: this.locale,
                },
            );
        }

        if (activity.activityType === ActivityType.Unblu) {
            return this.dateTimeService.convertDateToViewFriendlyFormat(
                (activity.updatedOn || activity.start) as unknown as string,
                { format: WdxDateFormat.RelativeDateTime, locale: this.locale },
            );
        }

        return this.dateTimeService.convertDateToViewFriendlyFormat(
            activity.start as unknown as string,
            { format: WdxDateFormat.RelativeDateTime, locale: this.locale },
        );
    }

    getActivityStatusDate(activity: Activity): string {
        if (this.isTaskOrWorkItem(activity)) {
            return activity.due as unknown as string;
        }
        return activity.start as unknown as string;
    }
}
